import { Injectable, Inject } from '@angular/core';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { catchError, map, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { CacheInMemoryService } from '@app/services/cache-in-memory.service';
import { Environment } from '@environments/interface.environment';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { Project, ProjectQueryResponse } from '@app/models/project';
import { ProjectQuery } from '@app/models/project-query';
import addMinutes from 'date-fns/esm/addMinutes';

@Injectable({
  providedIn: 'root'
})
export class ProjectService {

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment, private http: HttpClient, private cache: CacheInMemoryService) { }

  public useCache = true;
  private readonly cacheKeyPrefix = 'ProjectService';

  public flushCache() {
    this.cache.removeAllitemsInMemoryCacheWithPrefix(this.cacheKeyPrefix);
  }

  //query(q: ProjectQuery): Observable<Array<Project>> {
  query(q: ProjectQuery): Observable<ProjectQueryResponse> {

    const cacheKey = `${this.cacheKeyPrefix}-query-${JSON.stringify(q)}`;
    if (this.useCache) {
      //const cachedData = this.cache.get<Array<Project>>(cacheKey);
      const cachedData = this.cache.get<ProjectQueryResponse>(cacheKey);
      if (cachedData) {
        return of(cachedData);
      }
    }

    //const fetchOperation = this.http.post<Array<Project>>(`${Constants.API_ENDPOINT}/Projects/query/`, q, {
    const fetchOperation = this.http.post<ProjectQueryResponse>(`${Constants.API_ENDPOINT}/Projects/query/`, q, {
      ...HttpHelper.defaultRequestOptions
    })
      .pipe(
        map(response => {
          console.log(`Fetched projects:`, response);
          //return response.map(data => new Project(data));
          return response;
        }),
        tap(data => {
          this.useCache && this.cache.set(cacheKey, data, addMinutes(new Date(), 15));
        }),
        //catchError(HttpHelper.handleError('getAllProjects', new Array<Project>(), true)));
        catchError(HttpHelper.handleError('getAllProjects', new ProjectQueryResponse(), true)));

    return fetchOperation;
  }

  getProject(pdpProjectId: string): Observable<Project> {

    const cacheKey = `${this.cacheKeyPrefix}-getProject-${pdpProjectId}`;

    const fetchOperation = this.http.get<Project>(`${Constants.API_ENDPOINT}/Projects/${pdpProjectId}`, {
      ...HttpHelper.defaultRequestOptions,
    })
      .pipe(
        map(response => {
          console.log(`Fetched project:`, response);
          return new Project(response);
        }),
        tap(data => {
          this.useCache && this.cache.set(cacheKey, data);
        }),
        catchError(HttpHelper.handleError('getProject', new Project(), true)));

    if (this.useCache) {
      const cachedData = this.cache.get<Project>(cacheKey);
      if (cachedData) {
        return of(cachedData);
      }
    }
    return fetchOperation;
  }

  //// Create a project
  // addProject(project: Project, rethrowError = true): Observable<Project> {

  //  const fetchOperation = this.http.post<Project>(`${Constants.API_ENDPOINT}/Projects/`, project, {
  //    ...HttpHelper.defaultRequestOptions
  //  })
  //    .pipe(
  //      map(response => {
  //        if (this.useCache) {
  //          this.flushCache();
  //        }
  //        return response as Project;
  //      }),
  //      catchError(HttpHelper.handleError('addProject', null, rethrowError)));

  //  return fetchOperation;
  // }

  updateProject(project: Project): Observable<Project> {
    const fetchOperation = this.http.put<Project>(`${Constants.API_ENDPOINT}/Projects/`, project, {
      ...HttpHelper.defaultRequestOptions
    })
      .pipe(
        map(response => {
          this.flushCache();
          return response as Project;
        }),
        catchError(HttpHelper.handleError('updateProject', new Project(), true))
      );

    return fetchOperation;
  }

  closeProject(project: Project): Observable<Boolean> {
    const fetchOperation = this.http.put<Project>(`${Constants.API_ENDPOINT}/Projects/${project.PdpProjectId}/Close`, {
      ...HttpHelper.defaultRequestOptions
    })
      .pipe(
        map(response => {
          this.flushCache();
          return true;
        }),
        catchError(HttpHelper.handleError('closeProject', false, true))
      );

    return fetchOperation;
  }

  archiveProject(project: Project): Observable<Boolean> {
    const fetchOperation = this.http.put<Project>(`${Constants.API_ENDPOINT}/Projects/${project.PdpProjectId}/Archive`, {
      ...HttpHelper.defaultRequestOptions
    })
      .pipe(
        map(response => {
          this.flushCache();
          return true;
        }),
        catchError(HttpHelper.handleError('archiveProject', false, true))
      );

    return fetchOperation;
  }

  // deleteProject(pdpProjectId: number): Observable<boolean> {
  //  const fetchOperation = this.http.delete<Project>(`${Constants.API_ENDPOINT}/Projects/${pdpProjectId}`, {
  //    ...HttpHelper.defaultRequestOptions
  //  })
  //    .pipe(
  //      map(response => {
  //        this.flushCache();
  //        return true;
  //      }),
  //      catchError(HttpHelper.handleError('deleteProject', false, true)));

  //  return fetchOperation;
  // }


  applyTemplate(pdpProjectId: number, templateName: string): Observable<boolean> {

    const fetchOperation = this.http.put<string>(`${Constants.API_ENDPOINT}/Projects/${pdpProjectId}/Template/${templateName}`, {}, {
      ...HttpHelper.defaultRequestOptions
    })
      .pipe(
        map(response => {
          console.log(`Queued to apply provisioning template to site:`, response);
          return true;
        }),

        catchError(HttpHelper.handleError('applyTemplate', false, true)));

    return fetchOperation;
  }

  applyDefaultTemplate(pdpProjectId: number): Observable<boolean> {

    const fetchOperation = this.http.put<string>(`${Constants.API_ENDPOINT}/Projects/${pdpProjectId}/Template/Default`, {}, {
      ...HttpHelper.defaultRequestOptions
    })
      .pipe(
        map(response => {
          console.log(`Queued to apply default template to site:`, response);
          return true;
        }),

        catchError(HttpHelper.handleError('applyTemplate', false, true)));

    return fetchOperation;
  }

  applyDefaultRetentionLabel(pdpProjectId: number): Observable<boolean> {

    const fetchOperation = this.http.put<string>(`${Constants.API_ENDPOINT}/Projects/${pdpProjectId}/Retention/Default`, {}, {
      ...HttpHelper.defaultRequestOptions
    })
      .pipe(
        map(response => {
          console.log(`Queued to apply default retention label to site:`, response);
          return true;
        }),

        catchError(HttpHelper.handleError('applyDefaultRetentionLabel', false, true)));

    return fetchOperation;
  }

}
